import { useNavigate } from "react-router-dom"
import { MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { Grid } from "@mui/material"
import { useMemo, useState } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { ExtraServicesQuery, fetchExtraServices, FetchExtraServicesResponse } from "../extraServicesApi"
import { ExternalServiceEntry } from "../extraServicesTypes"
import {
    defaultConfirmModalState,
    DefaultConfirmModalStateType,
    DeleteExtraServiceDialog,
} from "../components/DeleteExtraServiceDialog"
import { renderRowActionMenuItems } from "../components/renderRowActionMenuItems"

const transformProductsData = (data: FetchExtraServicesResponse) => data.services

export function ExtraServices() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const services = useApiQuery(fetchExtraServices, {
        queryName: ExtraServicesQuery.LIST,
        dependencies: {
            merchantId: merchantId,
        },
        select: transformProductsData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(services)

    const columns = useMemo<MRT_ColumnDef<ExternalServiceEntry>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                header: l10n.getString("extra-services-services-column"),
            }),
            columnFactory.moneyColumn(
                {
                    id: "minimumPrice",
                    header: l10n.getString("extra-services-min-price-column"),
                },
                (row) => row.minimumPrice,
            ),
            columnFactory.moneyColumn(
                {
                    id: "maximumPrice",
                    header: l10n.getString("extra-services-max-price-column"),
                },
                (row) => row.maximumPrice,
            ),
            columnFactory.moneyColumn(
                {
                    id: "pricePerSeat",
                    header: l10n.getString("extra-services-price-per-seat-column"),
                },
                (row) => row.pricePerSeat,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("extra-services-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="extra-services-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Extra Services"
                    page={Page.MERCHANT_EXTRA_SERVICES}
                    rightElement={
                        <LocalizedStrict id="extra-services-create-service-button">
                            <StandardButton
                                onClick={() => {
                                    navigate(MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE)
                                }}
                            >
                                Create extra service
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <Breadcrumb>Extra services</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) =>
                        renderRowActionMenuItems({ row, closeMenu, setConfirmModalState, navigate })
                    }
                />

                {/* Confirm delete */}
                {confirmModalState.showConfirmModal && (
                    <DeleteExtraServiceDialog
                        confirmModalState={confirmModalState}
                        setConfirmModalState={setConfirmModalState}
                    />
                )}
            </Grid>
        </>
    )
}
